import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyApplication,
  TechnologyCertificate,
  TechnologyCertificateUpsertPayload,
  TechnologyInfrastructureAsset,
  TechnologySecret,
  createTechnologyCertificate,
  deleteTechnologyCertificate,
  listTechnologyApplications,
  listTechnologyCertificates,
  listTechnologyInfrastructureAssets,
  listTechnologySecrets,
  updateTechnologyCertificate,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_CERTIFICATE_EXPIRATION_OPTIONS,
  TECHNOLOGY_CERTIFICATE_KIND_OPTIONS,
  TECHNOLOGY_CERTIFICATE_STATUS_OPTIONS,
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

function defaultExpiry(): string {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() + 1);
  return date.toISOString().slice(0, 10);
}

const emptyForm: TechnologyCertificateUpsertPayload = {
  name: '',
  kind: 'Ssl',
  issuer: '',
  subjectName: '',
  expiresAtUtc: defaultExpiry(),
  status: 'Active',
  applicationId: '',
  environmentKind: '',
  infrastructureAssetId: '',
  secretId: '',
  notes: '',
};

function readProblemDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: { detail?: string } } }).response;
  return response?.data?.detail ?? null;
}

function expirationTone(state: string): string {
  switch (state) {
    case 'Expired':
      return 'bg-rose-100 text-rose-800';
    case 'Expiring':
      return 'bg-amber-100 text-amber-900';
    case 'Revoked':
      return 'bg-secondary-200 text-secondary-700';
    default:
      return 'bg-emerald-100 text-emerald-800';
  }
}

export function TechnologyCertificatesPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.certificates');
  const [items, setItems] = useState<TechnologyCertificate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [expirationFilter, setExpirationFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyCertificateUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [applications, setApplications] = useState<TechnologyApplication[]>([]);
  const [assets, setAssets] = useState<TechnologyInfrastructureAsset[]>([]);
  const [secrets, setSecrets] = useState<TechnologySecret[]>([]);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyCertificates({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        expiration: expirationFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao carregar certificados.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, expirationFilter]);

  useEffect(() => {
    void listTechnologyApplications({ page: 1, pageSize: 200 })
      .then((result) => setApplications(result.items))
      .catch(() => setApplications([]));
    void listTechnologyInfrastructureAssets({ page: 1, pageSize: 200 })
      .then((result) => setAssets(result.items))
      .catch(() => setAssets([]));
    void listTechnologySecrets({ page: 1, pageSize: 200 })
      .then((result) => setSecrets(result.items))
      .catch(() => setSecrets([]));
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setForm({ ...emptyForm, expiresAtUtc: defaultExpiry() });
  }

  function startEdit(item: TechnologyCertificate): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      kind: item.kind,
      issuer: item.issuer ?? '',
      subjectName: item.subjectName,
      expiresAtUtc: item.expiresAtUtc.slice(0, 10),
      status: item.status,
      applicationId: item.applicationId,
      environmentKind: item.environmentKind ?? '',
      infrastructureAssetId: item.infrastructureAssetId ?? '',
      secretId: item.secretId ?? '',
      notes: item.notes ?? '',
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyCertificateUpsertPayload = {
      ...form,
      issuer: form.issuer?.trim() || null,
      environmentKind: form.environmentKind || null,
      infrastructureAssetId: form.infrastructureAssetId || null,
      secretId: form.secretId || null,
      notes: form.notes?.trim() || null,
      expiresAtUtc: form.expiresAtUtc ? `${form.expiresAtUtc.slice(0, 10)}T12:00:00.000Z` : '',
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyCertificate(editingId, payload);
        toast.success('Certificado atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologyCertificate(payload);
        toast.success('Certificado criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao salvar certificado.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyCertificate): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir certificado',
      message: `Deseja excluir "${item.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyCertificate(item.id);
      toast.success('Certificado excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao excluir certificado.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Certificados"
        description="SSL, A1, A3 e certificados de API. A lista marca o que vence em 30 dias ou já venceu."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar certificado' : 'Novo certificado'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingId ? `Editando ${form.name || 'certificado'}` : undefined}
          createSummary="Clique para expandir e cadastrar um certificado"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  {TECHNOLOGY_CERTIFICATE_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Nome (CN)
                <input
                  className="erp-input"
                  value={form.subjectName}
                  onChange={(event) => setForm({ ...form, subjectName: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Emissor
                <input
                  className="erp-input"
                  value={form.issuer ?? ''}
                  onChange={(event) => setForm({ ...form, issuer: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Expira em
                <input
                  type="date"
                  className="erp-input"
                  value={form.expiresAtUtc.slice(0, 10)}
                  onChange={(event) => setForm({ ...form, expiresAtUtc: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Situação cadastral
                <select
                  className="erp-input"
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  {TECHNOLOGY_CERTIFICATE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Aplicação
                <select
                  className="erp-input"
                  value={form.applicationId}
                  onChange={(event) =>
                    setForm({ ...form, applicationId: event.target.value })
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {applications.map((application) => (
                    <option key={application.id} value={application.id}>
                      {application.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Ambiente
                <select
                  className="erp-input"
                  value={form.environmentKind ?? ''}
                  onChange={(event) => setForm({ ...form, environmentKind: event.target.value })}
                >
                  <option value="">Nenhum</option>
                  {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Ativo de infraestrutura
                <select
                  className="erp-input"
                  value={form.infrastructureAssetId ?? ''}
                  onChange={(event) =>
                    setForm({ ...form, infrastructureAssetId: event.target.value })
                  }
                >
                  <option value="">Nenhum</option>
                  {assets.map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {asset.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Credencial no cofre
                <select
                  className="erp-input"
                  value={form.secretId ?? ''}
                  onChange={(event) => setForm({ ...form, secretId: event.target.value })}
                >
                  <option value="">Nenhuma</option>
                  {secrets.map((secret) => (
                    <option key={secret.id} value={secret.id}>
                      {secret.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome, CN ou aplicação"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[220px]"
          value={expirationFilter}
          onChange={(event) => {
            setPage(1);
            setExpirationFilter(event.target.value);
          }}
        >
          <option value="">Todas as situações</option>
          {TECHNOLOGY_CERTIFICATE_EXPIRATION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Aplicação</th>
                  <th>Expira em</th>
                  <th>Situação</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 6 : 5} className="text-center text-secondary-500">
                      Nenhum certificado cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{technologyOptionLabel(TECHNOLOGY_CERTIFICATE_KIND_OPTIONS, item.kind)}</td>
                      <td>{item.applicationName || '—'}</td>
                      <td>{item.expiresAtUtc.slice(0, 10)}</td>
                      <td>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${expirationTone(item.expirationState)}`}
                        >
                          {technologyOptionLabel(
                            TECHNOLOGY_CERTIFICATE_EXPIRATION_OPTIONS,
                            item.expirationState,
                          )}
                        </span>
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
